import { Redirect, useRouter } from 'expo-router';
import { ActivityIndicator } from 'react-native';
import { Paragraph, View, YStack } from 'tamagui';

import { Heading } from '@/components/heading';
import { PrimaryButton } from '@/components/primary-button';
import { ScreenContainer } from '@/components/screen-container';
import { TextLink } from '@/components/text-link';
import { Wordmark } from '@/components/wordmark';
import { useAuth } from '@/hooks/auth-context';
import { useTier0State } from '@/hooks/use-tier0-state';
import { borderWidth } from '@/theme/tokens.generated';

// Concentric-circle badge -- echoes the brand mark's stamped-seal
// motif (see components/logo-mark.tsx) while reading, on this screen,
// as a camera lens/aperture. Built from plain themed Views rather than
// an SVG so every color stays a real Tamagui token (no manual
// light/dark resolution needed) -- gives the screen a real focal point
// instead of the bare centered-text-block the placeholder had (the
// "empty state owns its viewport" composition rule). Colocated here
// rather than promoted to src/components/: nothing else uses it yet
// (expo-design-system's promotion rule is two real call sites first).
function CaptureBadge() {
  return (
    <View
      width={88}
      height={88}
      borderRadius="$full"
      borderWidth={borderWidth.control}
      borderColor="$color"
      ai="center"
      jc="center"
    >
      <View width={40} height={40} borderRadius="$full" backgroundColor="$primary" />
    </View>
  );
}

// Real screen, not a stub. Deliberately sober, direct copy -- no
// playful "community noticeboard" motif -- per issue #11's scoping
// note on keeping safety-critical surfaces distinct in tone (same
// reasoning the placeholder this replaces already followed).
//
// Renders directly under the root Stack (headerShown: false globally,
// see src/app/_layout.tsx), so it carries its own Wordmark rather than
// relying on a native header -- the one placeholder that actually
// needed `showWordmark: true`, and the same is true of the real screen.
export function VerificationPending() {
  const { phase, capture, openSettings, canOpenSettings } = useTier0State();
  const router = useRouter();
  const auth = useAuth();

  // Opt-in flow (issue #70): the route is a plain Stack.Screen, so a
  // signed-out or needs-signup user deep-linking here would render a
  // capture screen whose submit could only 401. Bounce to the root --
  // the auth gate re-routes from there -- instead of rendering a flow
  // that can't complete.
  if (auth.status !== 'signed-up') {
    return <Redirect href="/" />;
  }

  return (
    <ScreenContainer>
      <YStack pb="$4">
        <Wordmark />
      </YStack>

      <YStack flex={1} alignItems="center" justifyContent="center" gap="$6">
        <CaptureBadge />

        {phase.phase === 'passed' ? (
          // Opt-in flow (issue #70): passing no longer flips the auth
          // gate (there is no gate), so the screen hands control back
          // explicitly instead of waiting for a state swap that won't
          // come. "Done" (not router.back() on auto) keeps the pass
          // moment deliberate -- the user chose to do this.
          <YStack gap="$2">
            {/* "Confirmed", not "Verified" -- see profile/index.tsx's own
                comment on this same badge: Tier 0 only confirms liveness,
                not identity, and design.md reserves "Verified" for Tier 1
                once it exists. Founder call, 2026-09-15. */}
            <Heading textAlign="center">You&apos;re confirmed</Heading>
            <Paragraph color="$colorSecondary" textAlign="center">
              Your profile now shows you&apos;re a confirmed real person.
            </Paragraph>
            <PrimaryButton onPress={() => router.back()}>Done</PrimaryButton>
          </YStack>
        ) : phase.phase === 'permission-denied' ? (
          <>
            <YStack gap="$2">
              <Heading textAlign="center">Camera access needed</Heading>
              <Paragraph color="$colorSecondary" textAlign="center">
                Nearkin needs camera access to verify you&apos;re a real person — enable it in Settings.
              </Paragraph>
            </YStack>

            <PrimaryButton onPress={canOpenSettings ? openSettings : capture}>
              {canOpenSettings ? 'Open Settings' : 'Try again'}
            </PrimaryButton>
            {canOpenSettings && (
              <TextLink textAlign="center" onPress={capture}>
                Try again
              </TextLink>
            )}
          </>
        ) : (
          <>
            <YStack gap="$2">
              <Heading textAlign="center">Verify it&apos;s you</Heading>
              <Paragraph color="$colorSecondary" textAlign="center">
                Take a quick photo so neighbors know there&apos;s a real person behind this account. Verification is
                optional -- browsing and posting never required it.
              </Paragraph>
            </YStack>

            <PrimaryButton
              loading={phase.phase === 'submitting'}
              onPress={() => {
                if (phase.phase !== 'submitting') capture();
              }}
            >
              {phase.phase === 'submitting' ? (
                <ActivityIndicator color="white" />
              ) : phase.phase === 'retry' || phase.phase === 'error' ? (
                'Try again'
              ) : (
                'Take a photo'
              )}
            </PrimaryButton>

            {phase.phase === 'retry' && (
              <Paragraph color="$colorSecondary" textAlign="center">
                That didn&apos;t work — give it another try.
              </Paragraph>
            )}
            {phase.phase === 'error' && (
              <Paragraph color="$destructive" textAlign="center">
                {phase.message}
              </Paragraph>
            )}
          </>
        )}

        {phase.phase !== 'passed' && (
          <TextLink
            color="$colorTertiary"
            fontSize="$meta"
            lineHeight="$meta"
            textAlign="center"
            onPress={() => router.back()}
          >
            Not now
          </TextLink>
        )}
      </YStack>
    </ScreenContainer>
  );
}
